"use client";

import Link from "next/link";
import Image from "next/image";
import { BlogPost } from "@/types/blog";

interface BlogCardProps {
  post: BlogPost;
}

export default function BlogCard({ post }: BlogCardProps) {
  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  return (
    <article className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 group">
      {/* Featured Image */}
      <Link
        href={`/blog/${post.slug}`}
        className="block relative aspect-[16/10] overflow-hidden"
      >
        {post.featuredImage ? (
          <Image
            src={post.featuredImage}
            alt={post.title}
            fill
            className="object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#7771B8] to-[#6B5B9A] flex items-center justify-center">
            <div className="text-center text-white p-6">
              <div className="text-2xl font-bold mb-2">UKWeightloss</div>
              <div className="text-sm opacity-90">Health & Wellness Blog</div>
            </div>
          </div>
        )}


        {/* Featured Badge */}
        {post.featured && (
          <div className="absolute top-4 right-4 z-10">
            <span className="inline-flex items-center px-3 py-1.5 bg-yellow-400 rounded-lg text-xs font-bold text-gray-900 shadow-md">
              ⭐ Featured
            </span>
          </div>
        )}
      </Link>

      {/* Content */}
      <div className="p-6">
        {/* Meta Info */}
        <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
          <time dateTime={post.publishedAt.toISOString()}>
            {formatDate(post.publishedAt)}
          </time>
          {post.readTime && (
            <>
              <span className="text-gray-300">•</span>
              <span>{post.readTime} min read</span>
            </>
          )}
        </div>

        {/* Title */}
        <Link href={`/blog/${post.slug}`}>
          <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-[#7771B8] transition-colors duration-200">
            {post.title}
          </h3>
        </Link>

        {/* Excerpt */}
        <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">
          {post.excerpt}
        </p>

        {/* Author & Read More */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="flex items-center gap-3">
            {post.author.avatar ? (
              <Image
                src={post.author.avatar}
                alt={post.author.name}
                width={32}
                height={32}
                className="rounded-full"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-[#7771B8] flex items-center justify-center text-white text-xs font-bold">
                {post.author.name.charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <div className="text-sm font-semibold text-gray-900">
                {post.author.name}
              </div>
              {post.author.title && (
                <div className="text-xs text-gray-500">{post.author.title}</div>
              )}
            </div>
          </div>

          <Link
            href={`/blog/${post.slug}`}
            className="text-[#7771B8] font-semibold text-sm hover:text-[#6B5B9A] transition-colors duration-200 flex items-center gap-1 group/link"
          >
            Read More
            <svg
              className="w-4 h-4 transform group-hover/link:translate-x-1 transition-transform duration-200"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </Link>
        </div>
      </div>
    </article>
  );
}
